import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { 
  Dialog, 
  DialogContent, 
  IconButton, 
  Typography,
  Box,
  LinearProgress,
  Slider,
  Tooltip,
  Fade,
  useTheme,
  useMediaQuery
} from "@material-ui/core";
import {
  Close,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  GetApp,
  Share,
  RotateLeft,
  RotateRight,
  ZoomIn,
  ZoomOut,
  AspectRatio
} from "@material-ui/icons";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

/**
 * 🔧 Normaliza URLs de mídia para dev/prod (mesma lógica do feed)
 * - Dev: ajusta localhost:443 -> 8080
 * - Prod: ancora /public no mesmo host e promove http->https em páginas https
 */
const normalizeMediaUrl = (url) => {
  if (!url) return url;
  let u = url;
  if (u.includes('localhost:443/')) {
    u = u.replace('localhost:443/', 'localhost:8080/');
  }
  try {
    const loc = window.location;
    const isHttpsPage = loc.protocol === 'https:';
    const urlObj = new URL(u, loc.origin);
    if (u.startsWith('/public/')) {
      urlObj.protocol = loc.protocol;
      urlObj.host = loc.host;
    }
    if (isHttpsPage && urlObj.protocol === 'http:') {
      urlObj.protocol = 'https:';
    }
    return urlObj.toString();
  } catch {
    return u;
  }
};

// 🎨 Estilos para o visualizador avançado de mídia
const useAdvancedMediaViewerStyles = makeStyles((theme) => ({
  dialog: {
    zIndex: 9999,
    '& .MuiDialog-paper': {
      backgroundColor: 'rgba(0, 0, 0, 0.96)',
      width: '95vw',
      height: '95vh',
      maxWidth: '95vw',
      maxHeight: '95vh',
      margin: theme.spacing(1),
      borderRadius: theme.spacing(1),
    },
  },
  dialogContent: {
    padding: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.96)'
  },
  mediaContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.96)'
  },
  media: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transition: 'transform 0.3s ease-in-out',
  },
  topBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, transparent 100%)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    zIndex: 1,
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'white',
    backgroundColor: 'rgba(0,0,0,0.35)',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
    zIndex: 2,
  },
  navLeft: { left: 8 },
  navRight: { right: 8 },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, transparent 100%)',
    padding: theme.spacing(2),
    zIndex: 1,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  progressSlider: {
    flexGrow: 1,
    minWidth: 160,
    color: theme.palette.primary.main,
  },
  volumeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 120,
  },
  volumeSlider: {
    width: 80,
    color: theme.palette.primary.main,
  },
  iconButton: {
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  timeDisplay: {
    color: 'white',
    fontSize: '0.875rem',
    minWidth: 60,
    textAlign: 'center',
  },
  title: {
    color: 'white',
    fontSize: '1rem',
    fontWeight: 500,
  },
  loadingContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: 'white',
  },
  errorContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: 'white',
    textAlign: 'center',
  },
}));

/**
 * 🎬 AdvancedMediaViewer - Visualizador avançado de mídia
 * 
 * Recursos:
 * - Reprodução de vídeo com controles customizados
 * - Zoom e rotação para imagens
 * - Controles de volume
 * - Modo fullscreen
 * - Download e compartilhamento
 * - Design moderno e responsivo
 */
const AdvancedMediaViewer = ({ 
  open, 
  onClose, 
  mediaUrl, 
  mediaType, 
  title,
  onDownload,
  onShare,
  items = null,
  currentIndex = 0,
  onNavigate = null,
  // 🎛️ Props de personalização dos controles
  showZoomControls = true,
  showRotateControls = true,
  showFullscreenButton = true,
  showDownloadButton = true,
  showShareButton = true,
  showVolumeControls = true,
  customButtons = [], // Array de botões customizados
}) => {
  const classes = useAdvancedMediaViewerStyles();
  const [activeIndex, setActiveIndex] = useState(currentIndex || 0);

  useEffect(() => {
    setActiveIndex(currentIndex || 0);
  }, [currentIndex, open]);

  const hasGallery = Array.isArray(items) && items.length > 0;
  const activeItem = hasGallery ? items[activeIndex] : null;
  const effectiveUrl = hasGallery ? normalizeMediaUrl(activeItem?.url) : normalizeMediaUrl(mediaUrl);
  const effectiveTitle = hasGallery ? (activeItem?.title || title || 'Mídia') : (title || 'Mídia');
  const effectiveType = hasGallery ? (activeItem?.type || mediaType) : mediaType;

  const goPrev = useCallback(() => {
    if (!hasGallery) return;
    setIsLoading(true);
    setHasError(false);
    const nextIndex = (activeIndex - 1 + items.length) % items.length;
    setActiveIndex(nextIndex);
    if (onNavigate) onNavigate(nextIndex, items[nextIndex]);
    setScale(1); setPanX(0); setPanY(0); setRotation(0);
  }, [activeIndex, hasGallery, items, onNavigate]);

  const goNext = useCallback(() => {
    if (!hasGallery) return;
    setIsLoading(true);
    setHasError(false);
    const nextIndex = (activeIndex + 1) % items.length;
    setActiveIndex(nextIndex);
    if (onNavigate) onNavigate(nextIndex, items[nextIndex]);
    setScale(1); setPanX(0); setPanY(0); setRotation(0);
  }, [activeIndex, hasGallery, items, onNavigate]);

  // Pré-carregamento da imagem anterior e próxima para navegação suave
  useEffect(() => {
    if (!hasGallery) return;
    const len = items.length;
    const prevIdx = (activeIndex - 1 + len) % len;
    const nextIdx = (activeIndex + 1) % len;
    const preload = (idx) => {
      const it = items[idx];
      if (!it || it.type !== 'image' || !it.url) return;
      const img = new Image();
      img.src = normalizeMediaUrl(it.url);
    };
    preload(prevIdx);
    preload(nextIdx);
  }, [hasGallery, items, activeIndex]);

  // Navegação por teclado
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!hasGallery) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, hasGallery, goNext, goPrev]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // 🎥 Estados de vídeo
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // 🖼️ Estados de imagem
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  
  // 🖱️ Estados de pan/drag para imagens com zoom
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [maxPan, setMaxPan] = useState({ x: 0, y: 0 });
  
  // 🏗️ Estados gerais
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showControls, setShowControls] = useState(true);
  
  // Refs
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const rafRef = useRef(null);
  const pendingPanRef = useRef({ x: 0, y: 0, has: false });
  const controlsTimeoutRef = useRef(null);

  // 🎯 Formatador de tempo
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // 🎯 Handlers de vídeo
  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused || video.ended) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }, []);

  const handleVolumeChange = useCallback((event, newValue) => {
    const video = videoRef.current;
    if (!video) return;

    const sliderValue = Array.isArray(newValue) ? newValue[0] : newValue;
    const volumeValue = Math.max(0, Math.min(1, (sliderValue || 0) / 100));
    setVolume(volumeValue);
    video.volume = volumeValue;
    setIsMuted(volumeValue === 0);
  }, []);

  const handleMute = useCallback(() => {
    if (!videoRef.current) return;
    
    if (isMuted) {
      videoRef.current.volume = volume;
      setIsMuted(false);
    } else {
      videoRef.current.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  const handleProgressChange = useCallback((event, newValue) => {
    const video = videoRef.current;
    if (!video || !duration || Number.isNaN(duration)) return;

    const sliderValue = Array.isArray(newValue) ? newValue[0] : newValue;
    const bounded = Math.max(0, Math.min(100, sliderValue || 0));
    const time = (bounded / 100) * duration;
    video.currentTime = time;
    setCurrentTime(time);
  }, [duration]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration || 0);
    setIsLoading(false);
  }, []);

  const handleVideoEnd = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setIsPlaying(false);
    const finalTime = video.duration || video.currentTime || 0;
    setCurrentTime(finalTime);
  }, []);

  // 🎯 Handlers de imagem
  const recalcPanBounds = useCallback(() => {
    if (!containerRef.current || !imageRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const img = imageRef.current;
    const naturalW = img.naturalWidth || img.videoWidth || 0;
    const naturalH = img.naturalHeight || img.videoHeight || 0;
    if (!naturalW || !naturalH) {
      setMaxPan({ x: 0, y: 0 });
      return;
    }
    const rot = ((rotation % 360) + 360) % 360;
    const rotated = rot === 90 || rot === 270;
    const baseW = rotated ? naturalH : naturalW;
    const baseH = rotated ? naturalW : naturalH;
    // A imagem é exibida com object-fit: contain ocupando o container em scale=1.
    const containerW = container.width;
    const containerH = container.height;
    // Em contain, a dimensão renderizada base (scale=1) é limitada pela menor razão
    const scaleContain = Math.min(containerW / baseW, containerH / baseH);
    const renderW = baseW * scaleContain * scale;
    const renderH = baseH * scaleContain * scale;
    const maxX = Math.max(0, (renderW - containerW) / 2);
    const maxY = Math.max(0, (renderH - containerH) / 2);
    setMaxPan({ x: maxX, y: maxY });
    // Clamp pan atual dentro dos novos limites
    setPanX(prev => Math.max(-maxX, Math.min(maxX, prev)));
    setPanY(prev => Math.max(-maxY, Math.min(maxY, prev)));
  }, [rotation, scale]);

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 0.25, 0.25));
  }, []);

  const handleRotateLeft = useCallback(() => {
    setRotation(prev => prev - 90);
  }, []);

  const handleRotateRight = useCallback(() => {
    setRotation(prev => prev + 90);
  }, []);

  const handleFitScreen = useCallback(() => {
    setScale(1);
    setRotation(0);
    setPanX(0);
    setPanY(0);
  }, []);

  // 🎯 Handlers gerais
  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  // 🖱️ Handlers de pan/drag para imagens com zoom
  const handleImageMouseDown = useCallback((e) => {
    if (scale <= 1) return;
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setPanStart({ x: panX, y: panY });
    e.preventDefault();
  }, [scale, panX, panY]);

  // 📱 Handlers para touch (mobile)
  const handleTouchStart = useCallback((e) => {
    if (scale <= 1 || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY });
    setPanStart({ x: panX, y: panY });
    e.preventDefault();
  }, [scale, panX, panY]);

  // Reset pan quando zoom = 1
  useEffect(() => {
    if (scale <= 1) {
      setPanX(0);
      setPanY(0);
    }
    recalcPanBounds();
  }, [scale, recalcPanBounds]);

  // 🎯 Effects
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setHasError(false);

    if (videoRef.current) {
      const video = videoRef.current;

      setIsLoading(true);
      try {
        video.currentTime = 0;
      } catch {}

      const handleTimeUpdate = () => setCurrentTime(video.currentTime || 0);
      const handleDurationChange = () => setDuration(video.duration || 0);
      const handleLoadStart = () => setIsLoading(true);
      const handleLoadedData = () => {
        setDuration(video.duration || 0);
        setIsLoading(false);
      };
      const handleError = () => {
        setIsLoading(false);
        setHasError(true);
      };
      const handleEnded = () => {
        setIsPlaying(false);
        const finalTime = video.duration || video.currentTime || 0;
        setCurrentTime(finalTime);
      };

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('durationchange', handleDurationChange);
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('durationchange', handleDurationChange);
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [mediaUrl]);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // ❌ Sem autoplay: a reprodução começa quando o usuário clicar em play

  // 🎯 Reset de estados quando o modal fechar
  useEffect(() => {
    if (!open) {
      setIsPlaying(false);
      setCurrentTime(0);
      setPanX(0);
      setPanY(0);
      setScale(1);
      setRotation(0);
    }
  }, [open]);

  // 🖱️ Event listeners globais para drag
  useEffect(() => {
    if (!isDragging) return;
    const updatePan = () => {
      if (pendingPanRef.current.has) {
        const { x, y } = pendingPanRef.current;
        setPanX(x);
        setPanY(y);
        pendingPanRef.current.has = false;
      }
      rafRef.current = requestAnimationFrame(updatePan);
    };
    rafRef.current = requestAnimationFrame(updatePan);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => {
        if (scale > 1) {
          const deltaX = e.clientX - dragStart.x;
          const deltaY = e.clientY - dragStart.y;
          const nextX = Math.max(-maxPan.x, Math.min(maxPan.x, panStart.x + deltaX));
          const nextY = Math.max(-maxPan.y, Math.min(maxPan.y, panStart.y + deltaY));
          pendingPanRef.current = { x: nextX, y: nextY, has: true };
        }
      };

      const handleGlobalTouchMove = (e) => {
        if (scale > 1 && e.touches.length === 1) {
          const touch = e.touches[0];
          const deltaX = touch.clientX - dragStart.x;
          const deltaY = touch.clientY - dragStart.y;
          const nextX = Math.max(-maxPan.x, Math.min(maxPan.x, panStart.x + deltaX));
          const nextY = Math.max(-maxPan.y, Math.min(maxPan.y, panStart.y + deltaY));
          pendingPanRef.current = { x: nextX, y: nextY, has: true };
          e.preventDefault();
        }
      };

      const handleGlobalEnd = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalEnd);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalEnd);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalEnd);
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalEnd);
      };
    }
  }, [isDragging, scale, dragStart, panStart, maxPan]);

  // Recalcular limites quando imagem carregar, em rotate, resize e fullscreen
  useEffect(() => {
    const recalc = () => recalcPanBounds();
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [recalcPanBounds, rotation, open]);

  // 🎯 Renderização de mídia
  const renderMedia = () => {
    if (hasError) {
      return (
        <Box className={classes.errorContainer}>
          <Typography variant="h6">Erro ao carregar mídia</Typography>
          <Typography variant="body2">Verifique sua conexão</Typography>
        </Box>
      );
    }

    if (effectiveType === 'video') {
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <video
            ref={videoRef}
            className={classes.media}
            src={effectiveUrl}
            preload="auto"
            onLoadStart={() => setIsLoading(true)}
            onLoadedData={() => setIsLoading(false)}
            onLoadedMetadata={handleLoadedMetadata}
            onError={() => setHasError(true)}
            onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
            onDurationChange={(e) => setDuration(e.target.duration)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={handleVideoEnd}
            playsInline
            muted={isMuted}
            controls={false}
          />
        </div>
      );
    }

    if (effectiveType === 'image') {
      return (
        <img
          ref={imageRef}
          className={classes.media}
          src={effectiveUrl}
          key={`img-${activeIndex || 0}-${effectiveUrl}`}
          alt={effectiveTitle}
          style={{
            transform: `translate(${panX}px, ${panY}px) scale(${scale}) rotate(${rotation}deg)`,
            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
            backgroundColor: 'rgba(0,0,0,0.96)',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out'
          }}
          onLoad={() => setIsLoading(false)}
          onError={() => setHasError(true)}
          onMouseDown={handleImageMouseDown}
          onTouchStart={handleTouchStart}
          draggable={false}
        />
      );
    }

    return null;
  };

  // 🎯 Renderização de controles
  const renderControls = () => {
    if (effectiveType === 'video') {
      return (
        <Fade in={showControls}>
          <Box className={classes.bottomBar}>
            <Box
              className={classes.controls}
              style={{
                width: hasGallery ? '100%' : 'auto',
                justifyContent: hasGallery ? 'space-between' : 'center',
              }}
            >
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flex: hasGallery ? 1 : 'unset',
                  justifyContent: hasGallery ? 'flex-start' : 'center',
                }}
              >
                <IconButton
                  className={classes.iconButton}
                  onClick={handlePlayPause}
                  size="small"
                >
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>

                <Box className={classes.progressContainer}>
                  <Typography className={classes.timeDisplay}>
                    {formatTime(currentTime)}
                  </Typography>
                  <Slider
                    className={classes.progressSlider}
                    value={duration ? Math.max(0, Math.min(100, (currentTime / duration) * 100)) : 0}
                    onChange={handleProgressChange}
                    disabled={!duration}
                    size="small"
                  />
                  <Typography className={classes.timeDisplay}>
                    {formatTime(duration)}
                  </Typography>
                </Box>
              </Box>

              {/* 🔊 Controles de Volume */}
              {showVolumeControls && (
                <Box className={classes.volumeContainer}>
                  <IconButton
                    className={classes.iconButton}
                    onClick={handleMute}
                    size="small"
                  >
                    {isMuted ? <VolumeOff /> : <VolumeUp />}
                  </IconButton>
                  <Slider
                    className={classes.volumeSlider}
                    value={isMuted ? 0 : volume * 100}
                    onChange={handleVolumeChange}
                    size="small"
                  />
                </Box>
              )}

              {hasGallery && (
                <Typography className={classes.timeDisplay} style={{ minWidth: 80, textAlign: 'right' }}>
                  {activeIndex + 1} / {items.length}
                </Typography>
              )}
            </Box>
          </Box>
        </Fade>
      );
    }

    if (effectiveType === 'image') {
      return (
        <Fade in={showControls}>
          <Box className={classes.bottomBar}>
            <Box className={classes.controls} style={{ width: '100%', justifyContent: 'center', gap: 12 }}>
              {/* 🔍 Controles de Zoom */}
              {showZoomControls && (
                <>
                  <Tooltip title="Diminuir zoom">
                    <IconButton
                      className={classes.iconButton}
                      onClick={handleZoomOut}
                      size="small"
                    >
                      <ZoomOut />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Aumentar zoom">
                    <IconButton
                      className={classes.iconButton}
                      onClick={handleZoomIn}
                      size="small"
                    >
                      <ZoomIn />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              {/* 🔄 Controles de Rotação */}
              {showRotateControls && (
                <>
                  <Tooltip title="Girar à esquerda">
                    <IconButton
                      className={classes.iconButton}
                      onClick={handleRotateLeft}
                      size="small"
                    >
                      <RotateLeft />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Girar à direita">
                    <IconButton
                      className={classes.iconButton}
                      onClick={handleRotateRight}
                      size="small"
                    >
                      <RotateRight />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ajustar à tela">
                    <IconButton
                      className={classes.iconButton}
                      onClick={handleFitScreen}
                      size="small"
                    >
                      <AspectRatio />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              {/* 🖼️ Navegação da galeria centralizada */}
              {hasGallery && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tooltip title="Anterior">
                    <IconButton className={classes.iconButton} onClick={goPrev} size="small" aria-label="Anterior">
                      <ChevronLeft />
                    </IconButton>
                  </Tooltip>
                  <Typography className={classes.timeDisplay} style={{ minWidth: 72, textAlign: 'center' }}>
                    {activeIndex + 1} / {items.length}
                  </Typography>
                  <Tooltip title="Próximo">
                    <IconButton className={classes.iconButton} onClick={goNext} size="small" aria-label="Próximo">
                      <ChevronRight />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className={classes.dialog}
      PaperProps={{
        style: { backgroundColor: 'transparent' }
      }}
    >
      <DialogContent 
        className={classes.dialogContent}
        ref={containerRef}
        onMouseMove={handleMouseMove}
      >
        {/* Barra superior */}
        <Fade in={showControls}>
          <Box className={classes.topBar}>
            <Typography className={classes.title}>
              {title || 'Mídia'}
            </Typography>
            
            <Box>
              {/* 💾 Botão de Download */}
              {showDownloadButton && onDownload && (
                <Tooltip title="Download">
                  <IconButton
                    className={classes.iconButton}
                    onClick={onDownload}
                    size="small"
                  >
                    <GetApp />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* 📤 Botão de Share */}
              {showShareButton && onShare && (
                <Tooltip title="Compartilhar">
                  <IconButton
                    className={classes.iconButton}
                    onClick={onShare}
                    size="small"
                  >
                    <Share />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* 🔲 Botão de Fullscreen */}
              {showFullscreenButton && (
                <Tooltip title="Tela cheia">
                  <IconButton
                    className={classes.iconButton}
                    onClick={handleFullscreen}
                    size="small"
                  >
                    {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  </IconButton>
                </Tooltip>
              )}
              
              {/* 🎛️ Botões Customizados */}
              {customButtons.map((button, index) => (
                <Tooltip key={index} title={button.tooltip || button.label}>
                  <IconButton
                    className={classes.iconButton}
                    onClick={button.onClick}
                    size="small"
                  >
                    {button.icon}
                  </IconButton>
                </Tooltip>
              ))}
              
              <Tooltip title="Fechar">
                <IconButton
                  className={classes.iconButton}
                  onClick={onClose}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Fade>

        {/* Navegação de galeria */}
        {hasGallery && (
          <>
            <IconButton className={`${classes.navButton} ${classes.navLeft}`} onClick={goPrev} aria-label="Anterior">
              <ChevronLeft />
            </IconButton>
            <IconButton className={`${classes.navButton} ${classes.navRight}`} onClick={goNext} aria-label="Próximo">
              <ChevronRight />
            </IconButton>
          </>
        )}

        {/* Container de mídia */}
        <Box className={classes.mediaContainer}>
          {isLoading && (
            <Box className={classes.loadingContainer}>
              <LinearProgress color="primary" />
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Carregando...
              </Typography>
            </Box>
          )}
          
          {renderMedia()}
        </Box>

        {/* Controles */}
        {renderControls()}
      </DialogContent>
    </Dialog>
  );
};

AdvancedMediaViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mediaUrl: PropTypes.string.isRequired,
  mediaType: PropTypes.oneOf(['image', 'video']).isRequired,
  title: PropTypes.string,
  onDownload: PropTypes.func,
  onShare: PropTypes.func,
  // 🎛️ Props de personalização
  showZoomControls: PropTypes.bool,
  showRotateControls: PropTypes.bool,
  showFullscreenButton: PropTypes.bool,
  showDownloadButton: PropTypes.bool,
  showShareButton: PropTypes.bool,
  showVolumeControls: PropTypes.bool,
  customButtons: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    tooltip: PropTypes.string,
    label: PropTypes.string,
  })),
};

export default AdvancedMediaViewer;
